import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  User, Mail, Phone, MapPin, Calendar, IdCard, Trophy, Bell, LogOut, Shield,
  BadgeCheck, TrendingUp, Camera, Save, CheckCircle2, XCircle, Clock,
} from "lucide-react";
import { toast } from "sonner";

function Info({ icon: Icon, label, value }) {
  return (
    <div className="border border-jpa-border p-3 rounded-sm bg-white">
      <div className="text-[10px] font-bold uppercase tracking-widest text-jpa-primary flex items-center gap-1"><Icon className="w-3.5 h-3.5" /> {label}</div>
      <div className="font-semibold text-sm md:text-base mt-1 break-words">{value || "—"}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    approved: { c: "bg-green-100 text-green-800", i: CheckCircle2 },
    pending: { c: "bg-amber-100 text-amber-800", i: Clock },
    rejected: { c: "bg-red-100 text-red-800", i: XCircle },
    withdrawn: { c: "bg-slate-200 text-slate-700", i: XCircle },
  };
  const s = map[status] || map.pending;
  const I = s.i;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest rounded-sm ${s.c}`}>
      <I className="w-3 h-3" /> {status}
    </span>
  );
}

export default function ProfilePage() {
  const { user, logout, refreshMe } = useAuth();
  const nav = useNavigate();
  // Club-role users have their own dedicated dashboard
  useEffect(() => {
    if (user && user.role === "club") nav("/club/dashboard", { replace: true });
  }, [user, nav]);
  const [entries, setEntries] = useState([]);
  const [matches, setMatches] = useState([]);
  const [notif, setNotif] = useState({ items: [], unread: 0 });
  const [rankings, setRankings] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  const load = async () => {
    try {
      const [e, m, n] = await Promise.all([
        api.get("/me/entries"),
        api.get("/me/matches?limit=10"),
        api.get("/me/notifications"),
      ]);
      setEntries(e.data);
      setMatches(m.data);
      setNotif(n.data);
    } catch (err) { /* noop */ }
  };

  useEffect(() => {
    if (user) {
      load();
      setForm({
        name: user.name || "", phone: user.phone || "", district: user.district || "",
        gender: user.gender || "", dob: user.dob || "", dupr_id: user.dupr_id || "", pgid: user.pgid || "",
        picture: user.picture || "",
      });
      // If linked player exists, pull their rankings — for now use user's gender + player id lookup
      api.get("/rankings?gender=" + (user.gender || "M") + "&format=singles&age=19%2B&limit=100")
        .then((r) => setRankings(r.data.slice(0, 4))).catch(() => {});
    }
  }, [user]);

  if (!user) return null;

  const upcoming = entries.filter((e) => e.tournament?.status === "upcoming");
  const running = entries.filter((e) => e.tournament?.status === "running");
  const past = entries.filter((e) => e.tournament?.status === "past");
  const wins = matches.filter((m) => m.result === "won").length;

  const saveProfile = async () => {
    try {
      await api.put("/auth/me", form);
      toast.success("Profile updated");
      await refreshMe();
      setEditing(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    }
  };

  const markRead = async (id) => {
    try { await api.post(`/me/notifications/${id}/read`); load(); } catch { /* noop */ }
  };
  const markAllRead = async () => {
    try { await api.post("/me/notifications/read-all"); load(); } catch { /* noop */ }
  };

  return (
    <div data-testid="profile-page" className="max-w-[1400px] mx-auto px-4 md:px-8 py-8 md:py-12">
      {/* HERO */}
      <div className="relative overflow-hidden rounded-sm border border-jpa-border bg-gradient-to-br from-jpa-primary to-jpa-secondary text-white">
        <div className="absolute inset-0 grain opacity-30" />
        <div className="relative p-5 md:p-8 grid md:grid-cols-[auto_1fr_auto] gap-5 md:gap-8 items-center">
          <div className="relative">
            <div className="w-24 h-24 md:w-32 md:h-32 rounded-full bg-white/10 border-4 border-jpa-accent overflow-hidden grid place-items-center">
              {form.picture ? (
                <img src={form.picture} alt={user.name} className="w-full h-full object-cover" />
              ) : (
                <span className="font-display text-5xl">{user.name?.[0] || "P"}</span>
              )}
            </div>
            {editing && (
              <label className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-jpa-accent text-jpa-ink grid place-items-center cursor-pointer">
                <Camera className="w-4 h-4" />
                <input
                  type="url"
                  placeholder="Paste image URL"
                  className="hidden"
                  data-testid="profile-picture-input"
                  onChange={(e) => setForm({ ...form, picture: e.target.value })}
                />
              </label>
            )}
          </div>
          <div>
            <div className="text-[10px] md:text-xs font-bold uppercase tracking-widest text-jpa-accent">JPA Player</div>
            <h1 data-testid="profile-name" className="font-display text-4xl md:text-6xl tracking-wider mt-1 leading-none">{user.name}</h1>
            <div className="mt-2 text-white/85 text-sm break-words">{user.email}</div>
            <div className="mt-3 flex flex-wrap gap-2">
              {user.jpa_id && (
                <span data-testid="profile-jpa-id" className="inline-flex items-center gap-1 bg-jpa-accent text-jpa-ink px-2 py-1 text-[10px] font-bold uppercase tracking-widest rounded-sm"><IdCard className="w-3 h-3" /> {user.jpa_id}</span>
              )}
              {user.dupr_id && <span className="inline-flex items-center gap-1 bg-white/15 px-2 py-1 text-[10px] font-bold uppercase tracking-widest rounded-sm"><BadgeCheck className="w-3 h-3" /> DUPR {user.dupr_id}</span>}
              {user.pgid && <span className="inline-flex items-center gap-1 bg-white/15 px-2 py-1 text-[10px] font-bold uppercase tracking-widest rounded-sm"><IdCard className="w-3 h-3" /> PGID {user.pgid}</span>}
            </div>
          </div>
          <div className="flex flex-col gap-2 w-full md:w-auto">
            {user.role === "admin" && (
              <Link to="/admin"><Button variant="outline" className="w-full rounded-sm bg-transparent border-white/40 hover:bg-white hover:text-jpa-ink"><Shield className="w-4 h-4 mr-2" /> Admin</Button></Link>
            )}
            <Button data-testid="profile-logout" onClick={logout} variant="outline" className="rounded-sm bg-transparent border-white/40 hover:bg-white hover:text-jpa-ink"><LogOut className="w-4 h-4 mr-2" /> Log out</Button>
          </div>
        </div>

        {/* Stats strip */}
        <div className="relative grid grid-cols-2 md:grid-cols-4 border-t border-white/15">
          <Stat label="Upcoming" value={upcoming.length + running.length} />
          <Stat label="Past events" value={past.length} bordered />
          <Stat label="Wins" value={wins} bordered />
          <Stat label="Notifications" value={notif.unread} bordered />
        </div>
      </div>

      {/* MAIN TABS */}
      <Tabs defaultValue="details" className="mt-8">
        <TabsList className="bg-jpa-surface rounded-sm h-auto p-1 flex-wrap w-full md:w-auto">
          {[
            ["details", "Details"],
            ["rankings", "Rankings"],
            ["tournaments", "Tournaments"],
            ["matches", "Matches"],
            ["notifications", `Notifications${notif.unread ? ` (${notif.unread})` : ""}`],
          ].map(([k, l]) => (
            <TabsTrigger key={k} value={k} data-testid={`profile-tab-${k}`} className="uppercase tracking-widest text-[11px] md:text-sm font-bold data-[state=active]:bg-jpa-primary data-[state=active]:text-white rounded-sm px-3 md:px-5 py-2">{l}</TabsTrigger>
          ))}
        </TabsList>

        {/* DETAILS */}
        <TabsContent value="details" className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-2xl md:text-3xl tracking-wider">Personal details</h2>
            {!editing ? (
              <Button data-testid="profile-edit" onClick={() => setEditing(true)} variant="outline" className="rounded-sm uppercase tracking-widest text-xs font-bold">Edit</Button>
            ) : (
              <div className="flex gap-2">
                <Button data-testid="profile-cancel" variant="outline" onClick={() => setEditing(false)} className="rounded-sm">Cancel</Button>
                <Button data-testid="profile-save" onClick={saveProfile} className="bg-jpa-primary hover:bg-jpa-primary-hover text-white rounded-sm"><Save className="w-4 h-4 mr-1" /> Save</Button>
              </div>
            )}
          </div>
          {!editing ? (
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
              <Info icon={User} label="Full name" value={user.name} />
              <Info icon={Mail} label="Email" value={user.email} />
              <Info icon={Phone} label="Phone" value={user.phone} />
              <Info icon={User} label="Gender" value={user.gender === "M" ? "Male" : user.gender === "F" ? "Female" : user.gender} />
              <Info icon={Calendar} label="Date of birth" value={user.dob} />
              <Info icon={MapPin} label="District" value={user.district} />
              <Info icon={IdCard} label="DUPR ID" value={user.dupr_id} />
              <Info icon={IdCard} label="PGID" value={user.pgid} />
              <Info icon={IdCard} label="JPA User ID" value={user.user_id} />
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
              {[
                ["name", "Full name"], ["phone", "Phone"], ["district", "District"],
                ["gender", "Gender (M/F/Other)"], ["dob", "Date of birth (YYYY-MM-DD)"],
                ["dupr_id", "DUPR ID"], ["pgid", "PGID"], ["picture", "Profile picture URL"],
              ].map(([k, l]) => (
                <div key={k}>
                  <Label>{l}</Label>
                  <Input data-testid={`profile-edit-${k}`} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1 rounded-sm" />
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* RANKINGS */}
        <TabsContent value="rankings" className="mt-6">
          <h2 className="font-display text-2xl md:text-3xl tracking-wider">Current rankings</h2>
          <p className="text-sm text-jpa-muted mt-1">Your best ranking across all categories.</p>
          {rankings.length === 0 ? (
            <div className="mt-6 border border-dashed border-jpa-border p-8 text-center text-jpa-muted rounded-sm">
              You aren't yet ranked. Compete in a JPA-sanctioned tournament to appear on the leaderboard.
            </div>
          ) : (
            <div className="mt-4 grid sm:grid-cols-2 md:grid-cols-4 gap-3">
              {rankings.map((r) => (
                <div key={r.id} data-testid={`profile-rank-${r.id}`} className="border border-jpa-border p-4 rounded-sm bg-white">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-jpa-primary flex items-center gap-1"><TrendingUp className="w-3 h-3" /> {r.format} • {r.age}</div>
                  <div className="font-display text-5xl tracking-wider text-jpa-primary mt-2">#{r.rank}</div>
                  <div className="text-xs text-jpa-muted mt-1">{r.points} points</div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* TOURNAMENTS */}
        <TabsContent value="tournaments" className="mt-6">
          <div className="grid gap-6">
            <TournamentGroup title="Upcoming" items={upcoming} testKey="upcoming" empty="No upcoming tournaments." />
            <TournamentGroup title="Currently playing" items={running} testKey="running" empty="Not playing any active tournament." />
            <TournamentGroup title="Past" items={past} testKey="past" empty="No past tournaments yet." />
          </div>
        </TabsContent>

        {/* MATCHES */}
        <TabsContent value="matches" className="mt-6">
          <h2 className="font-display text-2xl md:text-3xl tracking-wider">Last 10 matches</h2>
          {matches.length === 0 ? (
            <div className="mt-6 border border-dashed border-jpa-border p-8 text-center text-jpa-muted rounded-sm">No match results yet.</div>
          ) : (
            <div className="mt-4 border border-jpa-border rounded-sm overflow-hidden">
              <div className="hidden md:grid grid-cols-12 bg-jpa-ink text-white py-3 px-4 text-[10px] font-bold uppercase tracking-widest">
                <div className="col-span-1">Result</div>
                <div className="col-span-4">Tournament</div>
                <div className="col-span-3">Opponent</div>
                <div className="col-span-2">Score</div>
                <div className="col-span-1">Round</div>
                <div className="col-span-1">Date</div>
              </div>
              {matches.map((m) => (
                <div key={m.id} data-testid={`match-row-${m.id}`} className="grid grid-cols-12 items-center py-3 px-4 border-t border-jpa-border text-sm gap-1">
                  <div className="col-span-2 md:col-span-1">
                    <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest rounded-sm ${m.result === "won" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>{m.result}</span>
                  </div>
                  <div className="col-span-10 md:col-span-4 font-semibold">{m.tournament_name}</div>
                  <div className="col-span-6 md:col-span-3 text-jpa-muted">vs {m.opponent_name}</div>
                  <div className="col-span-6 md:col-span-2 font-mono">{m.score}</div>
                  <div className="col-span-6 md:col-span-1 text-jpa-muted">{m.round || "—"}</div>
                  <div className="col-span-6 md:col-span-1 text-jpa-muted">{m.played_on}</div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* NOTIFICATIONS */}
        <TabsContent value="notifications" className="mt-6">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-2xl md:text-3xl tracking-wider">Notifications</h2>
            {notif.unread > 0 && (
              <Button data-testid="notif-read-all" onClick={markAllRead} variant="outline" className="rounded-sm uppercase tracking-widest text-xs font-bold">Mark all read</Button>
            )}
          </div>
          {notif.items.length === 0 ? (
            <div className="mt-6 border border-dashed border-jpa-border p-8 text-center text-jpa-muted rounded-sm">You're all caught up.</div>
          ) : (
            <div className="mt-4 space-y-2">
              {notif.items.map((n) => (
                <div key={n.id} data-testid={`notif-${n.id}`} className={`flex items-start gap-3 p-4 rounded-sm border ${n.read ? "border-jpa-border bg-white" : "border-jpa-primary bg-jpa-primary/5"}`}>
                  <Bell className={`w-5 h-5 mt-0.5 ${n.read ? "text-jpa-muted" : "text-jpa-primary"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm">{n.title}</div>
                    {n.body && <div className="text-sm text-jpa-muted mt-0.5">{n.body}</div>}
                    <div className="text-[10px] uppercase tracking-widest text-jpa-muted mt-1">{new Date(n.created_at).toLocaleString()}</div>
                  </div>
                  {!n.read && (
                    <button data-testid={`notif-read-${n.id}`} onClick={() => markRead(n.id)} className="text-xs uppercase tracking-widest font-bold text-jpa-primary hover:underline">Mark read</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ label, value, bordered }) {
  return (
    <div className={`p-4 md:p-5 ${bordered ? "border-l border-white/15" : ""}`}>
      <div className="text-[10px] font-bold uppercase tracking-widest text-jpa-accent">{label}</div>
      <div className="font-display text-3xl md:text-4xl tracking-wider mt-1">{value}</div>
    </div>
  );
}

function TournamentGroup({ title, items, empty, testKey }) {
  return (
    <div data-testid={`tournament-group-${testKey}`}>
      <div className="flex items-center gap-2 mb-3">
        <Trophy className="w-4 h-4 text-jpa-primary" />
        <h3 className="font-display text-xl tracking-wider">{title} ({items.length})</h3>
      </div>
      {items.length === 0 ? (
        <div className="border border-dashed border-jpa-border p-4 text-jpa-muted text-sm rounded-sm">{empty}</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((e) => (
            <Link key={e.id} to={`/tournaments/${e.tournament_id}`} className="border border-jpa-border bg-white p-4 rounded-sm hover:border-jpa-primary transition-colors">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-display text-lg tracking-wider truncate">{e.tournament_name}</div>
                  <div className="text-xs text-jpa-muted mt-1">{e.tournament?.location} • {e.tournament?.start_date}</div>
                  {e.category && <div className="text-xs text-jpa-muted mt-0.5">Category: {e.category}</div>}
                </div>
                <StatusBadge status={e.status} />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
